/**
 * Link — the canonical interactive-text component. Wraps `next/link` with
 * three visual variants that cover every internal link in the monorepo.
 *
 *   <Link href="/jobs/abc" variant="inline">Job posting</Link>   // prose / body
 *   <Link href="/jobs"     variant="nav">Jobs</Link>              // chrome / sidebar
 *   <Link href="/settings" variant="muted">Settings</Link>        // de-emphasized
 *
 * Bare `<Link>` from `next/link` SHOULD NOT appear in product code — use
 * this wrapper instead so link styling is centralized. The only exceptions
 * are blocks inside `packages/ui/` that are themselves the "link styling"
 * (e.g. NavRail items that paint their own active state).
 *
 * Variants:
 *   inline — cobalt text + hover underline. The default for any link in
 *            paragraph/body context. Pairs with the rest of the foreground
 *            text so a colored link reads as "interactive identity".
 *   nav    — foreground text, no color or underline. For chrome links
 *            where the surrounding container (e.g. `.nav-active`) signals
 *            state. Hover lightens the row, not the text.
 *   muted  — muted-foreground text, hover lifts to foreground. Use for
 *            secondary links (footer, breadcrumb crumbs, etc.).
 *
 * Style notes:
 *   - `underline-offset-4` keeps the underline off the descenders.
 *   - `focus-visible` rings inherit the global cobalt ring.
 */

import * as React from "react";
import { RouterLink, type RouterLinkProps } from "../router";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils";
import { objectUidFromHref } from "../path-label";
import { PeekAnchor } from "./object-link";

const linkVariants = cva(
  "transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
  {
    variants: {
      variant: {
        inline:
          "rounded-sm text-primary underline-offset-4 hover:underline decoration-primary/50",
        nav: "rounded-sm text-foreground hover:text-foreground",
        muted: "rounded-sm text-muted-foreground hover:text-foreground",
        /**
         * Pass-through: wrap a whole row / card / button-like region in a
         * link without inheriting any text-color or underline styling.
         * The caller's className owns all visuals; we just supply the
         * focus ring. Use this when the wrapped element is itself the
         * interactive chrome (e.g. a kanban card, table row).
         */
        unstyled: "",
      },
    },
    defaultVariants: {
      variant: "inline",
    },
  },
);

type LinkProps = Omit<RouterLinkProps, "className"> &
  VariantProps<typeof linkVariants> & {
    children?: React.ReactNode;
    className?: string;
  };

export function Link({
  href,
  variant = "inline",
  className,
  children,
  ...rest
}: LinkProps) {
  const cls = cn(linkVariants({ variant }), className);

  /**
   * A link to a canonical object page opens the object PEEK in place instead of
   * navigating (Matt, 2026-07-20: clicking a goal, a customer, an issue should
   * show the modal first, with the page one Expand away). `objectUidFromHref`
   * recognizes exactly the shape `objectHref()` produces, so this catches every
   * object link in the app — including the many in SERVER components — without
   * a single call site changing, and a new one can't forget it.
   *
   * The branch keeps this module server-safe: only the object case reaches
   * across the client boundary (`PeekAnchor`), so ordinary links stay a plain
   * `next/link` with no added client bundle.
   */
  const uid = typeof href === "string" ? objectUidFromHref(href) : null;
  if (uid) {
    return (
      <PeekAnchor href={href as string} uid={uid} className={cls} {...rest}>
        {children}
      </PeekAnchor>
    );
  }

  return (
    <RouterLink href={href} className={cls} {...rest}>
      {children}
    </RouterLink>
  );
}

export { linkVariants };
